import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Question, TestResults, questionsApi } from '../services/api/questions';
import { useAuthStore } from './authStore';
import { migrateHistoryEntries } from '../utils/historyMigration';

interface HistoryEntry {
  id: string;
  date: string;
  results: TestResults;
}

interface TestState {
  test_id: string | null;
  temp_user_id: string | null;
  questions: Question[];
  currentAnswers: { [key: string]: number };
  currentResults: TestResults | null;
  isLoading: boolean;
  error: string | null;
  isComplete: boolean;
  history: HistoryEntry[];
  isOffline: boolean;
  offlineAnswers: { [key: string]: number };
  fetchQuestions: () => Promise<void>;
  setAnswer: (questionId: string, value: number) => Promise<void>;
  submitTest: () => Promise<TestResults>;
  getProgress: () => number;
  reset: () => void;
  resetTest: () => void;
  setError: (error: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setOffline: (isOffline: boolean) => void;
  syncOfflineData: () => Promise<void>;
  saveOfflineAnswer: (questionId: string, value: number) => void;
}

export const useTestStore = create<TestState>()(
  persist(
    (set, get) => ({
      test_id: null,
      temp_user_id: null,
      questions: [],
      currentAnswers: {},
      currentResults: null,
      isLoading: false,
      error: null,
      isComplete: false,
      history: [],
      isOffline: false,
      offlineAnswers: {},

      setError: (error: string | null) => set({ error }),
      setIsLoading: (isLoading: boolean) => set({ isLoading }),
      setOffline: (isOffline: boolean) => set({ isOffline }),

      saveOfflineAnswer: (questionId: string, value: number) => {
        set(state => ({
          offlineAnswers: {
            ...state.offlineAnswers,
            [questionId]: value
          },
          currentAnswers: {
            ...state.currentAnswers,
            [questionId]: value
          }
        }));
        
        // Save to localStorage for persistence
        try {
          const state = get();
          localStorage.setItem('enneagram_offline_answers', JSON.stringify({
            answers: state.offlineAnswers,
            test_id: state.test_id,
            temp_user_id: state.temp_user_id,
            lastUpdated: new Date().toISOString()
          }));
        } catch (e) {
          console.warn('Failed to save offline answers:', e);
        }
      },

      syncOfflineData: async () => {
        const state = get();
        if (!state.isOffline || Object.keys(state.offlineAnswers).length === 0) {
          return;
        }

        try {
          set({ isLoading: true });
          
          // Sync offline answers
          for (const [questionId, value] of Object.entries(state.offlineAnswers)) {
            if (state.test_id) {
              await questionsApi.saveAnswer(state.test_id, {
                question_id: questionId,
                value,
                temp_user_id: state.temp_user_id
              });
            }
          }

          // Clear offline data after successful sync
          set({ 
            offlineAnswers: {},
            isLoading: false 
          });
          
          // Clear localStorage
          localStorage.removeItem('enneagram_offline_answers');
          
        } catch (error) {
          console.error('Failed to sync offline data:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      reset: () => {
        set(state => {
          // Create a new history entry with the current date
          const newHistoryEntry = state.currentResults 
            ? { 
                id: crypto.randomUUID(), 
                date: new Date().toISOString(), 
                results: state.currentResults 
              }
            : null;

          return {
            test_id: null,
            temp_user_id: null,
            questions: [],
            currentAnswers: {},
            isLoading: false,
            error: null,
            isComplete: false,
            history: newHistoryEntry 
              ? [...(state.history || []), newHistoryEntry]
              : (state.history || []),
            // Don't reset currentResults so it's available on the results page
          };
        });
      },

      // This function is needed for the Results.tsx component
      resetTest: () => {
        set(state => {
          // Create a new history entry with the current date
          const newHistoryEntry = state.currentResults 
            ? { 
                id: crypto.randomUUID(), 
                date: new Date().toISOString(), 
                results: state.currentResults 
              }
            : null;

          return {
            test_id: null,
            temp_user_id: null,
            questions: [],
            currentAnswers: {},
            isLoading: false,
            error: null,
            isComplete: false,
            history: newHistoryEntry 
              ? [...(state.history || []), newHistoryEntry]
              : (state.history || []),
            // Don't reset currentResults so it's available on the results page
          };
        });
      },

      fetchQuestions: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await questionsApi.getTestQuestions();
          set({
            test_id: response.test_id,
            temp_user_id: response.temp_user_id,
            questions: response.questions,
            currentAnswers: {},
            isLoading: false
          });
        } catch (error: any) {
          console.error('Error fetching questions:', error);
          set({
            error: error.message || 'Error fetching questions',
            isLoading: false
          });
        }
      },

      setAnswer: async (questionId: string, value: number) => {
        const state = get();
        if (!state.test_id) {
          console.log('No test_id found, fetching new questions');
          await state.fetchQuestions();
          return;
        }

        // IMMEDIATE UI UPDATE: Update local state first for instant visual feedback
        set(state => {
          const newAnswers = {
            ...state.currentAnswers,
            [questionId]: value
          };
          
          // Store current progress in localStorage for persistence
          try {
            const progress = Math.floor((Object.keys(newAnswers).length / state.questions.length) * 100);
            localStorage.setItem('four_temperaments_test_progress', JSON.stringify({
              progress,
              answeredCount: Object.keys(newAnswers).length,
              totalQuestions: state.questions.length,
              test_id: state.test_id,
              lastUpdated: new Date().toISOString()
            }));
          } catch (e) {
            console.warn('Failed to save progress to localStorage:', e);
          }
          
          return {
            currentAnswers: newAnswers
          };
        });

        // BACKGROUND NETWORK SYNC: Handle server sync asynchronously
        // Check if we're offline
        if (!navigator.onLine) {
          console.log('Offline: saving answer locally');
          state.saveOfflineAnswer(questionId, value);
          return;
        }

        try {
          await questionsApi.saveAnswer(state.test_id, {
            question_id: questionId,
            value,
            temp_user_id: state.temp_user_id
          });
          console.log(`Successfully synced answer for question ${questionId}`);
        } catch (error: any) {
          console.error('Error saving answer to server:', error);
          // If network error, save offline for later sync
          if (error.message?.includes('fetch') || error.message?.includes('network')) {
            console.log('Network error: saving answer offline for later sync');
            state.saveOfflineAnswer(questionId, value);
          } else {
            // For other errors, show user notification but keep the local answer
            console.warn('Server error while saving answer, keeping local state:', error.message);
            // Don't set error state to avoid disrupting the user experience
            // The answer is already saved locally and will sync later
          }
        }
      },

      submitTest: async () => {
        const state = get();
        if (!state.test_id) {
          console.error('Cannot submit test: no test_id found');
          throw new Error('No test session found');
        }

        // Validate that we have all answers
        const progress = get().getProgress();
        if (progress < 100) {
          console.error('Cannot submit test: not all questions answered');
          throw new Error('Please answer all questions before submitting');
        }

        set({ isLoading: true, error: null });
        try {
          console.log('Submitting test with:', {
            test_id: state.test_id,
            temp_user_id: state.temp_user_id,
            answers: state.currentAnswers
          });

          const results = await questionsApi.submitTest(state.test_id, {
            temp_user_id: state.temp_user_id,
            answers: state.currentAnswers
          });
          
          console.log('Received test results:', results);

          // Ensure results has all required properties
          if (!results || !results.primaryType || !results.scores) {
            throw new Error('Invalid results format received from server');
          }

          // Create a new history entry with current date and unique ID
          const newHistoryEntry = {
            id: crypto.randomUUID(),
            date: new Date().toISOString(),
            results
          };

          // Migrate any existing history entries that don't have date/id
          const migratedHistory = migrateHistoryEntries(get().history);

          // Update state with results
          set({
            currentResults: results,
            isComplete: true,
            isLoading: false,
            error: null,
            history: [...migratedHistory, newHistoryEntry]
          });

          // Update the user's profile to indicate test completion
          try {
            const authStore = useAuthStore.getState();
            if (authStore.user && authStore.updateUser) {
              console.log('Updating user profile with test completion status');
              authStore.updateUser({
                hasCompletedTest: true,
                enneagramType: results.primaryType
              });
            }
          } catch (updateError) {
            console.error('Failed to update user profile after test completion:', updateError);
            // Don't throw this error since the test submission was successful
          }

          console.log('Updated test store state:', get());
          return results;
        } catch (error: any) {
          console.error('Error submitting test:', error);
          set({
            error: error.message || 'Error submitting test',
            isLoading: false
          });
          throw error;
        }
      },

      getProgress: () => {
        const state = get();
        if (!state.questions.length) return 0;
        return Math.floor((Object.keys(state.currentAnswers).length / state.questions.length) * 100);
      }
    }),
    {
      name: 'four-temperaments-test-storage',
      partialize: (state) => ({
        history: state.history,
        currentResults: state.currentResults,
        offlineAnswers: state.offlineAnswers,
        test_id: state.test_id,
        temp_user_id: state.temp_user_id,
        currentAnswers: state.currentAnswers
      })
    }
  )
);